import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

const hrApi = axios.create({
  baseURL: `${API_BASE_URL}/hr`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
hrApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ Department Types ============
export interface Department {
  id: string;
  code: string;
  name: string;
  description?: string;
  parentId?: string;
  managerId?: string;
  budget?: number;
  costCenter?: string;
  isActive: boolean;
  companyId: string;
  parent?: Department;
  children?: Department[];
  employees?: Employee[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateDepartmentInput {
  code: string;
  name: string;
  description?: string;
  parentId?: string;
  managerId?: string;
  budget?: number;
  costCenter?: string;
  isActive?: boolean;
}

export interface UpdateDepartmentInput {
  code?: string;
  name?: string;
  description?: string;
  parentId?: string;
  managerId?: string;
  budget?: number;
  costCenter?: string;
  isActive?: boolean;
}

export interface DepartmentFilter {
  search?: string;
  isActive?: boolean;
  parentId?: string;
  includeChildren?: boolean;
}

// ============ Position Types ============
export interface Position {
  id: string;
  code: string;
  title: string;
  description?: string;
  departmentId: string;
  level: string;
  grading?: string;
  employmentType: string;
  minSalary?: number;
  maxSalary?: number;
  annualLeave?: number;
  responsibilities?: string;
  qualifications?: string;
  isActive: boolean;
  companyId: string;
  department?: Department;
  employees?: Employee[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePositionInput {
  code: string;
  title: string;
  description?: string;
  departmentId: string;
  level: string;
  grading?: string;
  employmentType?: string;
  minSalary?: number;
  maxSalary?: number;
  annualLeave?: number;
  responsibilities?: string;
  qualifications?: string;
  isActive?: boolean;
}

export interface UpdatePositionInput {
  code?: string;
  title?: string;
  description?: string;
  departmentId?: string;
  level?: string;
  grading?: string;
  employmentType?: string;
  minSalary?: number;
  maxSalary?: number;
  annualLeave?: number;
  responsibilities?: string;
  qualifications?: string;
  isActive?: boolean;
}

export interface PositionFilter {
  search?: string;
  departmentId?: string;
  level?: string;
  employmentType?: string;
  isActive?: boolean;
}

// ============ Employee Types ============
export interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  hkidNo?: string;
  gender?: string;
  maritalStatus?: string;
  nationality?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  departmentId?: string;
  positionId?: string;
  managerId?: string;
  salaryStructureId?: string; // Link to salary structure
  hireDate: string;
  probationEndDate?: string;
  confirmationDate?: string;
  terminationDate?: string;
  terminationReason?: string;
  lastWorkingDay?: string;
  employmentType?: string;
  workLocation?: string;
  salary: number;
  salaryType?: string;
  currentSalary?: number;
  currency?: string;
  payFrequency?: string;
  bankAccount?: {
    bankName: string;
    bankCode?: string;
    accountNumber: string;
  };
  taxId?: string;
  socialSecurityNo?: string;
  mpfAccountNo?: string;
  healthInsurance?: any;
  workPermitNo?: string;
  workPermitExpiry?: string;
  visaType?: string;
  visaExpiry?: string;
  profilePhoto?: string;
  bio?: string;
  languages?: string[];
  userId?: string;
  status: string;
  isActive: boolean;
  companyId: string;
  department?: Department;
  position?: Position;
  manager?: Employee;
  subordinates?: Employee[];
  salaryStructure?: SalaryStructure; // Include salary structure relation
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmployeeInput {
  employeeId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  hkidNo?: string;
  gender?: string;
  maritalStatus?: string;
  nationality?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  departmentId?: string;
  positionId?: string;
  managerId?: string;
  salaryStructureId?: string; // Link to salary structure
  hireDate: string;
  probationEndDate?: string;
  employmentType?: string;
  workLocation?: string;
  salary: number;
  salaryType?: string;
  currency?: string;
  payFrequency?: string;
  bankAccount?: {
    bankName: string;
    bankCode?: string;
    accountNumber: string;
  };
  taxId?: string;
  mpfAccountNo?: string;
  status?: string;
  isActive?: boolean;
}

export interface UpdateEmployeeInput {
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  hkidNo?: string;
  gender?: string;
  maritalStatus?: string;
  nationality?: string;
  address?: any;
  emergencyContact?: any;
  departmentId?: string;
  positionId?: string;
  managerId?: string;
  salaryStructureId?: string | null; // Link to salary structure (null to unassign)
  hireDate?: string;
  probationEndDate?: string;
  employmentType?: string;
  workLocation?: string;
  salary?: number;
  salaryType?: string;
  currency?: string;
  bankAccount?: {
    bankName?: string;
    bankCode?: string;
    accountNumber?: string;
  };
  status?: string;
  isActive?: boolean;
}

export interface EmployeeFilter {
  search?: string;
  departmentId?: string;
  positionId?: string;
  status?: string;
  employmentType?: string;
  managerId?: string;
  isActive?: boolean;
  hireDateFrom?: string;
  hireDateTo?: string;
}

// ============ Leave Request Types ============
export type LeaveType = 'VACATION' | 'SICK' | 'PERSONAL' | 'MATERNITY' | 'PATERNITY' | 'BEREAVEMENT' | 'UNPAID' | 'OTHER';
export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveRequest {
  id: string;
  employeeId: string;
  leaveType: LeaveType;
  startDate: string;
  endDate: string;
  reason: string;
  status: LeaveStatus;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
}

export interface CreateLeaveRequestInput {
  employeeId: string;
  leaveType: LeaveType;
  startDate: string;
  endDate: string;
  reason: string;
}

export interface UpdateLeaveRequestInput {
  leaveType?: LeaveType;
  startDate?: string;
  endDate?: string;
  reason?: string;
}

export interface LeaveRequestFilter {
  employeeId?: string;
  status?: LeaveStatus;
  leaveType?: LeaveType;
  startDate?: string;
  endDate?: string;
}

export interface LeaveBalance {
  leaveType: string;
  allocated: number;
  used: number;
  remaining: number;
}

export interface LeaveStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  byType: { leaveType: string; count: number }[];
  byStatus: { status: string; count: number }[];
}

// ============ Attendance Types ============
export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'EARLY_DEPARTURE' | 'HALF_DAY' | 'ON_LEAVE';

export interface AttendanceRecord {
  id: string;
  employeeId: string;
  date: string;
  checkIn?: string;
  checkOut?: string;
  status: AttendanceStatus;
  workHours?: number;
  overtimeHours?: number;
  location?: string;
  notes?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
}

export interface CreateAttendanceInput {
  employeeId: string;
  date: string;
  checkIn?: string;
  checkOut?: string;
  status?: AttendanceStatus;
  notes?: string;
  location?: string;
}

export interface UpdateAttendanceInput {
  checkIn?: string;
  checkOut?: string;
  status?: AttendanceStatus;
  notes?: string;
}

export interface AttendanceFilter {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
  status?: AttendanceStatus;
}

export interface AttendanceStats {
  totalPresent: number;
  totalAbsent: number;
  totalLate: number;
  totalOnLeave: number;
  averageWorkHours: number;
  byEmployee: { employeeId: string; present: number; absent: number; late: number }[];
}

// ============ Shift Types ============
export interface Shift {
  id: string;
  name: string;
  code: string;
  startTime: string;
  endTime: string;
  breakDuration?: number;
  workingDays?: string[];
  isActive: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateShiftInput {
  name: string;
  code: string;
  startTime: string;
  endTime: string;
  breakDuration?: number;
  workingDays?: string[];
  isActive?: boolean;
}

export interface UpdateShiftInput {
  name?: string;
  code?: string;
  startTime?: string;
  endTime?: string;
  breakDuration?: number;
  workingDays?: string[];
  isActive?: boolean;
}

// ============ Stats Types ============
export interface DepartmentStats {
  total: number;
  active: number;
  inactive: number;
  withManager: number;
  withoutManager: number;
}

export interface PositionStats {
  total: number;
  active: number;
  inactive: number;
  byLevel: { level: string; count: number }[];
  byEmploymentType: { employmentType: string; count: number }[];
}

export interface EmployeeStats {
  total: number;
  active: number;
  inactive: number;
  terminated: number;
  onLeave: number;
  byStatus: { status: string; count: number }[];
  byDepartment: { departmentId: string | null; departmentName: string | null; count: number }[];
  byEmploymentType: { employmentType: string | null; count: number }[];
}

// ============ Department API ============
export const departmentService = {
  getAll: async (filter?: DepartmentFilter): Promise<Department[]> => {
    const response = await hrApi.get('/departments', { params: filter });
    return response.data.data;
  },

  getById: async (id: string): Promise<Department> => {
    const response = await hrApi.get(`/departments/${id}`);
    return response.data.data;
  },

  getHierarchy: async (rootId?: string): Promise<Department[]> => {
    const response = await hrApi.get('/departments/hierarchy', { params: { rootId } });
    return response.data.data;
  },

  getChildren: async (id: string): Promise<Department[]> => {
    const response = await hrApi.get(`/departments/${id}/children`);
    return response.data.data;
  },

  getStats: async (): Promise<DepartmentStats> => {
    const response = await hrApi.get('/departments/stats');
    return response.data.data;
  },

  getEmployeeCount: async (id: string): Promise<number> => {
    const response = await hrApi.get(`/departments/${id}/employee-count`);
    return response.data.data.count;
  },

  create: async (data: CreateDepartmentInput): Promise<Department> => {
    const response = await hrApi.post('/departments', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateDepartmentInput): Promise<Department> => {
    const response = await hrApi.put(`/departments/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/departments/${id}`);
  },
};

// ============ Position API ============
export const positionService = {
  getAll: async (filter?: PositionFilter): Promise<Position[]> => {
    const response = await hrApi.get('/positions', { params: filter });
    return response.data.data;
  },

  getById: async (id: string): Promise<Position> => {
    const response = await hrApi.get(`/positions/${id}`);
    return response.data.data;
  },

  getByDepartment: async (departmentId: string): Promise<Position[]> => {
    const response = await hrApi.get(`/positions/department/${departmentId}`);
    return response.data.data;
  },

  getByLevel: async (level: string): Promise<Position[]> => {
    const response = await hrApi.get(`/positions/level/${level}`);
    return response.data.data;
  },

  getVacant: async (): Promise<Position[]> => {
    const response = await hrApi.get('/positions/vacant');
    return response.data.data;
  },

  getStats: async (): Promise<PositionStats> => {
    const response = await hrApi.get('/positions/stats');
    return response.data.data;
  },

  getEmployeeCount: async (id: string): Promise<number> => {
    const response = await hrApi.get(`/positions/${id}/employee-count`);
    return response.data.data.count;
  },

  create: async (data: CreatePositionInput): Promise<Position> => {
    const response = await hrApi.post('/positions', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdatePositionInput): Promise<Position> => {
    const response = await hrApi.put(`/positions/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/positions/${id}`);
  },
};

// ============ Employee API (v2) ============
export const employeeService = {
  getAll: async (filter?: EmployeeFilter): Promise<Employee[]> => {
    const response = await hrApi.get('/employees/v2', { params: filter });
    return response.data.data;
  },

  getById: async (id: string): Promise<Employee> => {
    const response = await hrApi.get(`/employees/v2/${id}`);
    return response.data.data;
  },

  getByEmployeeId: async (employeeId: string): Promise<Employee> => {
    const response = await hrApi.get(`/employees/v2/by-employee-id/${employeeId}`);
    return response.data.data;
  },

  getByDepartment: async (departmentId: string): Promise<Employee[]> => {
    const response = await hrApi.get(`/employees/v2/department/${departmentId}`);
    return response.data.data;
  },

  getByPosition: async (positionId: string): Promise<Employee[]> => {
    const response = await hrApi.get(`/employees/v2/position/${positionId}`);
    return response.data.data;
  },

  getOrgChart: async (rootId?: string): Promise<Employee[]> => {
    const response = await hrApi.get('/employees/v2/org-chart', { params: { rootId } });
    return response.data.data;
  },

  getSubordinates: async (id: string): Promise<Employee[]> => {
    const response = await hrApi.get(`/employees/v2/${id}/subordinates`);
    return response.data.data;
  },

  getStats: async (): Promise<EmployeeStats> => {
    const response = await hrApi.get('/employees/v2/stats');
    return response.data.data;
  },

  getNewHiresCount: async (startDate?: string): Promise<number> => {
    const response = await hrApi.get('/employees/v2/new-hires', { params: { startDate } });
    return response.data.data.count;
  },

  getTerminationsCount: async (startDate?: string): Promise<number> => {
    const response = await hrApi.get('/employees/v2/terminations', { params: { startDate } });
    return response.data.data.count;
  },

  getAverageSalary: async (): Promise<number> => {
    const response = await hrApi.get('/employees/v2/average-salary');
    return response.data.data.averageSalary;
  },

  create: async (data: CreateEmployeeInput): Promise<Employee> => {
    const response = await hrApi.post('/employees/v2', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateEmployeeInput): Promise<Employee> => {
    const response = await hrApi.put(`/employees/v2/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/employees/v2/${id}`);
  },

  // Lifecycle operations
  terminate: async (
    id: string,
    data: {
      terminationReason: 'RESIGNED' | 'RETIRED' | 'FIRED' | 'CONTRACT_ENDED' | 'OTHER';
      terminationNote?: string;
      terminationDate?: string;
      lastWorkingDay?: string;
      removeFromZkDevices?: boolean;
    }
  ): Promise<{
    employee: Employee;
    zk?: {
      attempted: number;
      removed: number;
      mappingsDeleted: number;
      errors: Array<{ deviceId: string; message: string }>;
    };
  }> => {
    const response = await hrApi.post(`/employees/v2/${id}/terminate`, data);
    return { employee: response.data.data, zk: response.data.zk };
  },

  reactivate: async (id: string): Promise<Employee> => {
    const response = await hrApi.post(`/employees/v2/${id}/reactivate`);
    return response.data.data;
  },

  setOnLeave: async (id: string): Promise<Employee> => {
    const response = await hrApi.post(`/employees/v2/${id}/on-leave`);
    return response.data.data;
  },

  returnFromLeave: async (id: string): Promise<Employee> => {
    const response = await hrApi.post(`/employees/v2/${id}/return-from-leave`);
    return response.data.data;
  },

  // Assign or unassign salary structure to employee
  assignSalaryStructure: async (employeeId: string, salaryStructureId: string | null): Promise<Employee> => {
    const response = await hrApi.put(`/employees/${employeeId}/salary-structure`, { salaryStructureId });
    return response.data.data;
  },
};

// ============ Leave Management API ============
export const leaveService = {
  getAll: async (filter?: LeaveRequestFilter): Promise<LeaveRequest[]> => {
    const response = await hrApi.get('/leave-requests', { params: filter });
    return response.data.data;
  },

  getById: async (id: string): Promise<LeaveRequest> => {
    const response = await hrApi.get(`/leave-requests/${id}`);
    return response.data.data;
  },

  getStats: async (year?: number): Promise<LeaveStats> => {
    const response = await hrApi.get('/leave-requests/stats', { params: { year } });
    return response.data.data;
  },

  getBalance: async (employeeId: string, year?: number): Promise<LeaveBalance[]> => {
    const response = await hrApi.get(`/employees/${employeeId}/leave-balance`, { params: { year } });
    return response.data.data;
  },

  create: async (data: CreateLeaveRequestInput): Promise<LeaveRequest> => {
    const response = await hrApi.post('/leave-requests', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateLeaveRequestInput): Promise<LeaveRequest> => {
    const response = await hrApi.put(`/leave-requests/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/leave-requests/${id}`);
  },

  approve: async (id: string): Promise<LeaveRequest> => {
    const response = await hrApi.put(`/leave-requests/${id}/approve`);
    return response.data.data;
  },

  reject: async (id: string, reason: string): Promise<LeaveRequest> => {
    const response = await hrApi.put(`/leave-requests/${id}/reject`, { reason });
    return response.data.data;
  },
};

// ============ Attendance Management API ============
export const attendanceService = {
  getAll: async (filter?: AttendanceFilter): Promise<AttendanceRecord[]> => {
    const response = await hrApi.get('/attendance', { params: filter });
    return response.data.data;
  },

  getStats: async (month?: string): Promise<AttendanceStats> => {
    const response = await hrApi.get('/attendance/stats', { params: { month } });
    return response.data.data;
  },

  create: async (data: CreateAttendanceInput): Promise<AttendanceRecord> => {
    const response = await hrApi.post('/attendance', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateAttendanceInput): Promise<AttendanceRecord> => {
    const response = await hrApi.put(`/attendance/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/attendance/${id}`);
  },

  checkIn: async (employeeId: string, location?: string): Promise<AttendanceRecord> => {
    const response = await hrApi.post('/attendance/check-in', { employeeId, location });
    return response.data.data;
  },

  checkOut: async (employeeId: string): Promise<AttendanceRecord> => {
    const response = await hrApi.post('/attendance/check-out', { employeeId });
    return response.data.data;
  },
};

// ============ Shift Management API ============
export const shiftService = {
  getAll: async (includeInactive?: boolean): Promise<Shift[]> => {
    const response = await hrApi.get('/shifts', { params: { includeInactive } });
    return response.data.data;
  },

  create: async (data: CreateShiftInput): Promise<Shift> => {
    const response = await hrApi.post('/shifts', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateShiftInput): Promise<Shift> => {
    const response = await hrApi.put(`/shifts/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/shifts/${id}`);
  },
};

// ============ Salary Structure Types ============
export type ContributionType = 'MPF' | 'CPF' | '401K' | 'PENSION' | 'OTHER';

export interface SalaryStructure {
  id: string;
  name: string;
  code?: string;
  description?: string;
  currency: string;
  baseSalary: number;
  // Hong Kong specific allowances
  housingAllowance: number;
  transportAllowance: number;
  mealAllowance: number;
  phoneAllowance: number;
  otherAllowances?: { name: string; amount: number }[];
  // Contribution settings (MPF/CPF/401K/Pension)
  employeeContribRate: number;
  employerContribRate: number;
  contribCapped: boolean;
  contribCapAmount: number;
  contribMinIncome: number;
  contribMaxIncome: number;
  contribType: ContributionType;
  // Tax settings
  taxName?: string;
  taxRate: number;
  otherDeductionRates?: { name: string; rate: number }[];
  isActive: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  // Relations
  employees?: Employee[];
  _count?: { employees: number };
}

export interface CreateSalaryStructureInput {
  name: string;
  code?: string;
  description?: string;
  currency?: string;
  baseSalary: number;
  housingAllowance?: number;
  transportAllowance?: number;
  mealAllowance?: number;
  phoneAllowance?: number;
  otherAllowances?: { name: string; amount: number }[];
  employeeContribRate?: number;
  employerContribRate?: number;
  contribCapped?: boolean;
  contribCapAmount?: number;
  contribMinIncome?: number;
  contribMaxIncome?: number;
  contribType?: ContributionType;
  taxName?: string;
  taxRate?: number;
  otherDeductionRates?: { name: string; rate: number }[];
  isActive?: boolean;
}

export interface UpdateSalaryStructureInput {
  name?: string;
  code?: string;
  description?: string;
  currency?: string;
  baseSalary?: number;
  housingAllowance?: number;
  transportAllowance?: number;
  mealAllowance?: number;
  phoneAllowance?: number;
  otherAllowances?: { name: string; amount: number }[];
  employeeContribRate?: number;
  employerContribRate?: number;
  contribCapped?: boolean;
  contribCapAmount?: number;
  contribMinIncome?: number;
  contribMaxIncome?: number;
  contribType?: ContributionType;
  taxName?: string;
  taxRate?: number;
  otherDeductionRates?: { name: string; rate: number }[];
  isActive?: boolean;
}

// ============ Payroll Record Types (Enhanced for HK) ============
export type PayrollStatus = 'PENDING' | 'PROCESSED' | 'APPROVED' | 'PAID' | 'CANCELLED';

export interface PayrollRecord {
  id: string;
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  currency: string;
  baseSalary: number;
  // Allowances
  housingAllowance: number;
  transportAllowance: number;
  mealAllowance: number;
  phoneAllowance: number;
  otherAllowances?: { name: string; amount: number }[];
  totalAllowances: number;
  // Contributions (MPF/CPF/401K/Pension)
  employeeContribution: number;
  employerContribution: number;
  contributionType: ContributionType;
  relevantIncome: number;
  // Deductions
  taxDeduction: number;
  socialSecurity: number;
  healthInsurance: number;
  loanDeduction: number;
  advanceDeduction: number;
  otherDeductions?: { name: string; amount: number }[];
  totalDeductions: number;
  // Earnings
  overtimeHours: number;
  overtimePay: number;
  bonuses: number;
  commissions: number;
  allowances: number;
  // Totals
  grossPay: number;
  netPay: number;
  // Status and workflow
  status: PayrollStatus;
  approvedBy?: string;
  approvedAt?: string;
  paidAt?: string;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  employee?: Employee;
}

export interface CreatePayrollRecordInput {
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  currency?: string;
  baseSalary: number;
  // Allowances
  housingAllowance?: number;
  transportAllowance?: number;
  mealAllowance?: number;
  phoneAllowance?: number;
  otherAllowances?: { name: string; amount: number }[];
  // Contribution settings
  employeeContribRate?: number;
  employerContribRate?: number;
  contribCapped?: boolean;
  contribCapAmount?: number;
  contribMinIncome?: number;
  contribMaxIncome?: number;
  contributionType?: ContributionType;
  // Deductions
  taxDeduction?: number;
  socialSecurity?: number;
  healthInsurance?: number;
  loanDeduction?: number;
  advanceDeduction?: number;
  otherDeductions?: { name: string; amount: number }[];
  // Earnings
  overtimeHours?: number;
  overtimePay?: number;
  bonuses?: number;
  commissions?: number;
  notes?: string;
}

export interface CreatePayrollFromStructureInput {
  employeeId: string;
  salaryStructureId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  workingDays?: number;
  overtimeHours?: number;
  bonuses?: number;
  additionalDeductions?: { name: string; amount: number }[];
  notes?: string;
}

// ============ Salary Structure API ============
export const salaryStructureService = {
  getAll: async (includeInactive?: boolean): Promise<SalaryStructure[]> => {
    const response = await hrApi.get('/salary-structures', { params: { includeInactive } });
    return response.data.data;
  },

  getById: async (id: string): Promise<SalaryStructure> => {
    const response = await hrApi.get(`/salary-structures/${id}`);
    return response.data.data;
  },

  getEmployees: async (id: string): Promise<Employee[]> => {
    const response = await hrApi.get(`/salary-structures/${id}/employees`);
    return response.data.data;
  },

  create: async (data: CreateSalaryStructureInput): Promise<SalaryStructure> => {
    const response = await hrApi.post('/salary-structures', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateSalaryStructureInput): Promise<SalaryStructure> => {
    const response = await hrApi.put(`/salary-structures/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await hrApi.delete(`/salary-structures/${id}`);
  },
};

// ============ Payroll API (Enhanced) ============
export interface CreatePayrollFromEmployeeInput {
  employeeId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  workingDays?: number;
  overtimeHours?: number;
  bonuses?: number;
  additionalDeductions?: { name: string; amount: number }[];
  notes?: string;
}

export const payrollService = {
  getAll: async (): Promise<PayrollRecord[]> => {
    const response = await hrApi.get('/payroll');
    return response.data.data;
  },

  create: async (data: CreatePayrollRecordInput): Promise<PayrollRecord> => {
    const response = await hrApi.post('/payroll', data);
    return response.data.data;
  },

  createFromStructure: async (data: CreatePayrollFromStructureInput): Promise<PayrollRecord> => {
    const response = await hrApi.post('/payroll/from-structure', data);
    return response.data.data;
  },

  // Create payroll from employee's assigned salary structure (auto-fetch)
  createFromEmployee: async (data: CreatePayrollFromEmployeeInput): Promise<PayrollRecord> => {
    const response = await hrApi.post('/payroll/from-employee', data);
    return response.data.data;
  },

  process: async (recordId: string): Promise<PayrollRecord> => {
    const response = await hrApi.put(`/payroll/${recordId}/process`);
    return response.data.data;
  },

  approve: async (recordId: string): Promise<PayrollRecord> => {
    const response = await hrApi.put(`/payroll/${recordId}/approve`);
    return response.data.data;
  },

  markAsPaid: async (recordId: string): Promise<PayrollRecord> => {
    const response = await hrApi.put(`/payroll/${recordId}/pay`);
    return response.data.data;
  },
};

// ============ Legacy HR API ============
export const hrLegacyService = {
  getDashboard: async () => {
    const response = await hrApi.get('/dashboard');
    return response.data.data;
  },

  getAnalytics: async () => {
    const response = await hrApi.get('/analytics');
    return response.data.data;
  },

  // Payroll (legacy - use payrollService instead)
  getPayrollRecords: async () => {
    const response = await hrApi.get('/payroll');
    return response.data.data;
  },

  createPayrollRecord: async (data: any) => {
    const response = await hrApi.post('/payroll', data);
    return response.data.data;
  },

  processPayroll: async (recordId: string) => {
    const response = await hrApi.put(`/payroll/${recordId}/process`);
    return response.data.data;
  },

  approvePayroll: async (recordId: string) => {
    const response = await hrApi.put(`/payroll/${recordId}/approve`);
    return response.data.data;
  },

  markPayrollAsPaid: async (recordId: string) => {
    const response = await hrApi.put(`/payroll/${recordId}/pay`);
    return response.data.data;
  },
};

export default {
  department: departmentService,
  position: positionService,
  employee: employeeService,
  leave: leaveService,
  attendance: attendanceService,
  shift: shiftService,
  salaryStructure: salaryStructureService,
  payroll: payrollService,
  legacy: hrLegacyService,
};
